"use client";

import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  Table as ReactTable,
  RowData,
  useReactTable,
} from "@tanstack/react-table";
import { ChevronRight } from "lucide-react";
import { Fragment, useEffect, useState } from "react";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./table";
import { Skeleton } from "./skeleton";
import { cn } from "../../lib/utils";

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  isLoading: boolean;
  className?: string;
  onTableRowClick?: (
    item: TData,
    options: { newTab: boolean },
  ) => void;
  renderExpandedRow?: (item: TData) => React.ReactNode;
  renderSubRow?: (item: TData) => React.ReactNode;
}

const renderSkeletonRows = <TData extends RowData>(
  table: ReactTable<TData>,
) => {
  return Array(5)
    .fill(0)
    .map((_, index) => (
      <TableRow key={`skeleton-${index}`} className="group">
        {table.getAllColumns().map((column) => (
          <TableCell
            key={`skeleton-cell-${column.id}`}
            className="first:pl-6 h-16"
            style={{
              width: column.getSize(),
              minWidth: 200,
            }}
          >
            <Skeleton className="h-4 w-full" />
          </TableCell>
        ))}
      </TableRow>
    ));
};

export function DataTable<TData, TValue>({
  columns,
  data,
  isLoading,
  className,
  onTableRowClick,
  renderExpandedRow,
  renderSubRow,
}: DataTableProps<TData, TValue>) {
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  useEffect(() => {
    setExpandedRows(new Set());
  }, [data]);

  const hasExpandableRows = !!renderExpandedRow;
  const colCount = columns.length + (hasExpandableRows ? 1 : 0);

  const toggleRow = (rowId: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) {
        next.delete(rowId);
      } else {
        next.add(rowId);
      }
      return next;
    });
  };

  return (
    <div className={cn("rounded-md border flex", className)}>
      <Table className="flex-1">
        <TableHeader className="w-full">
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {hasExpandableRows && (
                <TableHead className="w-7 p-0" />
              )}
              {headerGroup.headers.map((header) => {
                return (
                  <TableHead
                    key={header.id}
                    style={{ width: `${header.getSize()}px` }}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody className="w-full">
          {isLoading ? (
            renderSkeletonRows(table)
          ) : table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row) => {
              const isExpanded = expandedRows.has(row.id);
              return (
                <Fragment key={row.id}>
                  <TableRow
                    className={"hover:cursor-pointer"}
                    data-state={row.getIsSelected() && "selected"}
                    onClick={(e) =>
                      onTableRowClick?.(row.original, {
                        newTab: e.metaKey || e.ctrlKey,
                      })
                    }
                  >
                    {hasExpandableRows && (
                      <TableCell className="w-7 p-0 pl-1">
                        <button
                          type="button"
                          className="p-0.5 rounded hover:bg-muted"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleRow(row.id);
                          }}
                        >
                          <ChevronRight
                            className={`size-3.5 transition-transform ${isExpanded ? "rotate-90" : ""}`}
                          />
                        </button>
                      </TableCell>
                    )}
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                  {renderSubRow && (
                    <TableRow className="hover:bg-transparent">
                      <TableCell
                        colSpan={colCount}
                        className="p-0"
                      >
                        {renderSubRow(row.original)}
                      </TableCell>
                    </TableRow>
                  )}
                  {hasExpandableRows && isExpanded && (
                    <TableRow className="hover:bg-transparent">
                      <TableCell
                        colSpan={colCount}
                        className="p-0"
                      >
                        {renderExpandedRow(row.original)}
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              );
            })
          ) : (
            <TableRow>
              <TableCell colSpan={colCount} className="h-24 text-center">
                No results.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
